// Client-side PDF export of the tender detail view.
//
// Captures the rendered detail DOM (including the Leaflet address map) with
// html2canvas and lays the image out across A4 pages via @react-pdf/renderer.
// Both libraries are imported lazily so they stay out of the main bundle.

import { createElement } from "react";

const PAGE_WIDTH = 595.28; // A4 portrait in pt
const PAGE_HEIGHT = 841.89;
const MARGIN = 32;

export async function exportElementAsPdf(element: HTMLElement, filename: string): Promise<void> {
  const html2canvas = (await import("html2canvas")).default;
  const { Document: PdfDocument, Page: PdfPage, Image: PdfImage, pdf } = await import(
    "@react-pdf/renderer"
  );

  const canvas = await html2canvas(element, {
    scale: 2,
    // Reload images (map tiles) with CORS so the canvas isn't tainted.
    useCORS: true,
    backgroundColor: "#ffffff",
    // The detail body is a scroll container — un-clip it in the cloned
    // document so the full content is captured, not just the visible part.
    onclone: (clonedDocument) => {
      const clone = element.id ? clonedDocument.getElementById(element.id) : null;
      if (clone) {
        clone.style.maxHeight = "none";
        clone.style.overflow = "visible";
      }
    },
    height: element.scrollHeight,
    windowHeight: element.scrollHeight + 200,
  });

  // Slice the tall capture into A4-sized pages.
  const contentWidth = PAGE_WIDTH - 2 * MARGIN;
  const contentHeight = PAGE_HEIGHT - 2 * MARGIN;
  const pxPerPt = canvas.width / contentWidth;
  const pageHeightPx = Math.floor(contentHeight * pxPerPt);

  const pages: { src: string; heightPt: number }[] = [];
  for (let y = 0; y < canvas.height; y += pageHeightPx) {
    const sliceHeight = Math.min(pageHeightPx, canvas.height - y);
    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = sliceHeight;
    const context = slice.getContext("2d");
    if (!context) throw new Error("Canvas 2D context not available");
    context.drawImage(canvas, 0, y, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
    pages.push({ src: slice.toDataURL("image/jpeg", 0.92), heightPt: sliceHeight / pxPerPt });
  }

  const pdfDocument = createElement(
    PdfDocument,
    {},
    pages.map((page, index) =>
      createElement(
        PdfPage,
        { key: index, size: "A4", style: { padding: MARGIN } } as any,
        createElement(PdfImage, {
          src: page.src,
          style: { width: contentWidth, height: page.heightPt },
        } as any)
      )
    )
  );

  const blob = await pdf(pdfDocument as any).toBlob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

// Filesystem-safe name from the publication number / title.
export function pdfFilename(base: string): string {
  const safe = base
    .replace(/[^a-zA-Z0-9äöüÄÖÜß_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
  return `ausschreibung-${safe || "detail"}.pdf`;
}
